import { z } from 'zod';
import { doctenaConnectionModes, type DoctenaConnectionMode, type MemberRole } from '../enums.js';
import { trimmed } from './_fields.js';
import type { DoctenaSettings } from './doctena.js';

/**
 * Doctena **par praticien** : le mode de connexion du cabinet, l'état de chaque membre vu par le
 * propriétaire, et la connexion personnelle d'un praticien vue par lui-même.
 *
 * Aucun identifiant ici non plus. La clé d'un membre ne sort que par `POST …/me/reveal`, à lui
 * seul ; le propriétaire voit un état, jamais la clé ni l'email d'un autre.
 */

/* -------------------------------------------------------------------------- */
/* Mode de connexion                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Ce que devient la connexion du cabinet quand on passe en mode « par membre ». **Pas de défaut** :
 * le serveur refuse le changement (`doctena_mode_confirmation_required`) tant que l'écran n'a pas
 * fait choisir. `keep` la laisse en sommeil — elle reprend si l'on revient au mode cabinet —,
 * `delete` l'efface avec ses associations.
 */
export const doctenaOrganizationConnectionFates = ['keep', 'delete'] as const;
export type DoctenaOrganizationConnectionFate = (typeof doctenaOrganizationConnectionFates)[number];

export const doctenaConnectionModeInputSchema = z
  .object({
    mode: z.enum(doctenaConnectionModes),
    organizationConnection: z.enum(doctenaOrganizationConnectionFates).optional(),
  })
  .strict();
export type DoctenaConnectionModeInput = z.infer<typeof doctenaConnectionModeInputSchema>;

/* -------------------------------------------------------------------------- */
/* État d'une connexion personnelle                                            */
/* -------------------------------------------------------------------------- */

/**
 * - `connected` : identifiants lisibles, cabinet Doctena et calendrier choisis, dernier passage
 *   sans échec.
 * - `incomplete` : identifiants enregistrés, mais cabinet ou calendrier encore à choisir.
 * - `error` : clé illisible, ou dernier passage en échec.
 * - `not_configured` : rien d'enregistré.
 */
export const doctenaMemberStates = ['connected', 'incomplete', 'error', 'not_configured'] as const;
export type DoctenaMemberState = (typeof doctenaMemberStates)[number];

/** Une ligne du tableau du propriétaire. Ni clé, ni email du compte Doctena du membre. */
export interface DoctenaMemberStatusDto {
  memberId: string;
  firstName: string;
  lastName: string;
  role: MemberRole;
  /** La ligne du propriétaire lui-même : il règle la sienne dans « Ma connexion Doctena ». */
  isSelf: boolean;
  state: DoctenaMemberState;
  lastSyncAt: string | null;
}

export interface DoctenaModeDto {
  mode: DoctenaConnectionMode;
  /** Une connexion de cabinet existe, active ou en sommeil : le passage au mode membre le demande. */
  hasOrganizationConnection: boolean;
  /** Les praticiens actifs du cabinet. Les secrétaires n'ont pas d'agenda, donc pas de ligne. */
  members: DoctenaMemberStatusDto[];
}

/** « Ma connexion Doctena », pour le membre de la session et pour lui seul. */
export interface MyDoctenaConnectionDto {
  mode: DoctenaConnectionMode;
  /** Seul un praticien tient un agenda, donc peut avoir une connexion personnelle. */
  isPractitioner: boolean;
  state: DoctenaMemberState;
  hasCredentials: boolean;
  /** Faux quand la clé enregistrée ne se relit plus : l'écran ne propose que de la ressaisir. */
  credentialsReadable: boolean;
  /** L'email de **son** compte Doctena, pour qu'il sache lequel est relié. */
  accountEmail: string | null;
  isEnabled: boolean;
  settings: DoctenaSettings | null;
  /** Le calendrier Doctena relié à son agenda, ou nul. */
  linkedCalendarId: string | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastSyncAt: string | null;
  lastErrorAt: string | null;
}

/** Le calendrier de son compte qui alimente son agenda. `null` délie. */
export const doctenaMemberCalendarSchema = z
  .object({ calendarId: trimmed(64).nullable() })
  .strict();
export type DoctenaMemberCalendarInput = z.infer<typeof doctenaMemberCalendarSchema>;
